import {Button} from '@dropbox/dig-components/buttons';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ArrowLeftLine} from '@dropbox/dig-icons/assets';
import {Employee} from 'client';
import {t} from 'i18next';
import {useEffect, useRef, useState} from 'react';

import {RowPreview} from './RowPreview';
import {SearchRow} from './searchRows';

/** How long something has to stay in the pane to count as looked at, not swept past. */
const PANE_DWELL_MS = 1000;

/** How the pane's root got there: shown for you (the top result), clicked, or the arrow keys. */
export type PaneHow = 'auto' | 'click' | 'keyboard';

const titleOf = (row: SearchRow) =>
  row.kind === 'person'
    ? row.person.name
    : row.kind === 'meeting'
      ? row.meeting.title
      : row.kind === 'team'
        ? (row.team.name ?? t('team'))
        : row.kind === 'workstream'
          ? row.workstream.name
          : row.kind === 'knowledge'
            ? row.result.title
            : row.kind === 'news'
              ? row.post.title
              : row.question;

/**
 * The right pane: `root`, and a trail of things clicked inside it. Clicking a
 * person or team in the pane opens them here, with a link back, so walking the
 * org never leaves the box.
 */
export const Panel = ({
  root,
  onOpenPerson,
  onGo,
  onView,
  rootHow = 'auto',
  embedded = false,
}: {
  root: SearchRow;
  onOpenPerson: (person: Employee) => void;
  /** Go to a row (open an article). */
  onGo: (row: SearchRow) => void;
  /**
   * Something stayed in the pane long enough to be looked at: the root
   * (`depth` 0, shown `rootHow`) or something clicked open inside it.
   */
  onView?: (row: SearchRow, view: {depth: number; how: PaneHow}) => void;
  rootHow?: PaneHow;
  /** The root's title is already on screen (the question in the field): skip it. */
  embedded?: boolean;
}) => {
  const [trail, setTrail] = useState<SearchRow[]>([]);
  useEffect(() => setTrail([]), [root.id]);

  const current = trail[trail.length - 1] ?? root;
  const depth = trail.length;
  const onViewRef = useRef(onView);
  onViewRef.current = onView;
  useEffect(() => {
    const timer = window.setTimeout(
      () => onViewRef.current?.(current, {depth, how: depth > 0 ? 'click' : rootHow}),
      PANE_DWELL_MS
    );
    return () => window.clearTimeout(timer);
    // Once per thing shown; how it got there is as of when it arrived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.id, depth]);
  const previous = trail.length > 1 ? trail[trail.length - 2] : trail.length === 1 ? root : null;

  return (
    <Stack gap="12">
      {previous && (
        <Box>
          <Button
            variant="transparent"
            size="small"
            withIconStart={<UIIcon src={ArrowLeftLine} />}
            onClick={() => setTrail((rows) => rows.slice(0, -1))}
          >
            <Truncate maxWidth={360}>{titleOf(previous)}</Truncate>
          </Button>
        </Box>
      )}
      <RowPreview
        row={current}
        onOpenPerson={onOpenPerson}
        onGo={onGo}
        onSelect={(row) => setTrail((rows) => [...rows, row])}
        embedded={embedded && trail.length === 0}
        // The top result, shown while typing; anything else was picked.
        startedBy={depth === 0 && rootHow === 'auto' ? 'typing' : 'click'}
      />
    </Stack>
  );
};
